import { useCallback, useRef, useState } from 'react';
import type { ApiError } from '@app/api/ApiError';
import { useAsk } from './Chat.api';
import { askerSub, type AgentAskResult, type Asker } from './ChatModel';

/**
 * O relógio da janela da conversa (aim-92fo): o relógio de parede é lido UMA vez, no envio, e o fim é o início mais o
 * tempo que passou no relógio monotônico. Duas leituras do relógio de parede podiam dar um fim antes do início, quando o
 * relógio da máquina recua (o WSL2 desta máquina recua 87 s a cada 30 s), e o link de Decisões (T16) saía com
 * `until` antes de `since`. As duas fontes se injetam para o teste.
 */
export function conversationClock(
  wall: () => number = () => Date.now(),
  monotonic: () => number = () => performance.now(),
): { startedAt: string; finishedAt: () => string } {
  const startedMs = wall();
  const startedMono = monotonic();
  return {
    startedAt: new Date(startedMs).toISOString(),
    finishedAt: () => new Date(startedMs + Math.max(0, monotonic() - startedMono)).toISOString(),
  };
}

/** Uma linha da transcrição. */
export type ChatMessage =
  | {
      id: string;
      role: 'person';
      /** O `sub` de quem perguntou. */
      who: string;
      text: string;
    }
  | {
      id: string;
      role: 'agent';
      who: string;
      /** `null` enquanto a resposta não chegou. */
      result: AgentAskResult | null;
      /**
       * The conversation of the trail and of the link «Ver as decisões desta conversa» (aim-6ph.9): the person and the
       * window of the question (sent → answered). The window is the browser's clock, and the trail widens it by 5 s on
       * each side. The agent is `who`.
       */
      sub: string;
      startedAt: string;
      finishedAt: string | null;
      /**
       * The id of the question (aim-6ph.10). The screen creates it before it sends the question, so it has the id even
       * when the answer does not come.
       */
      conversationId: string;
    };

/**
 * A random UUID in lower case, the only form the gateway accepts (aim-6ph.10). `crypto.randomUUID` exists only in a
 * secure context (HTTPS or localhost): a console opened by plain HTTP on another address builds the same form from
 * `getRandomValues`, which exists everywhere.
 */
export function newConversationId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export interface ChatSend {
  agent: string;
  asker: Asker;
  question: string;
}

export interface Chat {
  messages: ChatMessage[];
  send: (send: ChatSend) => void;
  /** `true` enquanto uma pergunta está em voo. */
  sending: boolean;
  /** Erro do último envio — a rota recusou a **operação** (não é resposta do agente). */
  error: ApiError | null;
  clearError: () => void;
  /** Esvazia a transcrição. */
  reset: () => void;
}

/**
 * A conversa: a transcrição vive no estado da tela e some ao sair — o que ficou
 * gravado está na trilha (`agent.ask`, T23), e um histórico próprio seria uma
 * cópia envelhecendo sozinha (a mesma decisão de T25).
 *
 * Cada envio vira **duas** linhas: a pergunta, e a resposta pendente que a
 * bolha do agente mostra como «pensando…» até o REST voltar. Erro da rota tira
 * a pendente da conversa e vira o banner — a pergunta fica, para ser reenviada.
 */
export function useChat(): Chat {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const ask = useAsk();
  const sequence = useRef(0);

  const send = useCallback(
    ({ agent, asker, question }: ChatSend) => {
      const id = String(++sequence.current);
      const who = askerSub(asker);
      const clock = conversationClock();
      const startedAt = clock.startedAt;
      const conversationId = newConversationId();
      setError(null);
      setMessages((previous) => [
        ...previous,
        { id: `${id}-q`, role: 'person', who, text: question },
        {
          id: `${id}-a`,
          role: 'agent',
          who: agent,
          result: null,
          sub: who,
          startedAt,
          finishedAt: null,
          conversationId,
        },
      ]);

      ask.mutate(
        {
          agent,
          question,
          conversationId,
          ...(asker.kind === 'person' ? { person: asker.person.sub } : { claims: asker.claims }),
        },
        {
          onSuccess: (result) => {
            setMessages((previous) =>
              previous.map((message) =>
                message.id === `${id}-a` && message.role === 'agent'
                  ? { ...message, who: result.agent, result, finishedAt: clock.finishedAt() }
                  : message,
              ),
            );
          },
          onError: (failure) => {
            setError(failure);
            setMessages((previous) => previous.filter((message) => message.id !== `${id}-a`));
          },
        },
      );
    },
    [ask],
  );

  return {
    messages,
    send,
    sending: ask.isPending,
    error,
    clearError: useCallback(() => setError(null), []),
    reset: useCallback(() => {
      setMessages([]);
      setError(null);
    }, []),
  };
}
